import React, { useEffect, useMemo, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  IconButton,
  Box,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { FiArrowUp, FiArrowDown } from 'react-icons/fi'
import type { RpcHealthSnapshot } from '@/types'

interface RpcGlobalOrderModalProps {
  isOpen: boolean
  onClose: () => void
  snapshots: RpcHealthSnapshot[]
  isSubmitting: boolean
  onSubmit: (providerOrder: string[]) => void
}

const providerColorMap: Record<string, string> = {
  alchemy: 'blue',
  helius: 'cyan',
  ankr: 'purple',
  public: 'teal',
}

const normalizeProvider = (providerType: unknown): string => {
  if (typeof providerType === 'string' && providerType.trim().length > 0) {
    const normalized = providerType.trim().toLowerCase()
    if (normalized === 'custom') {
      return 'public'
    }
    return normalized
  }
  return 'public'
}

const buildInitialOrder = (snapshots: RpcHealthSnapshot[]): string[] => {
  const seen = new Set<string>()
  const order: string[] = []

  snapshots.forEach(snapshot => {
    snapshot.endpoints.forEach(endpoint => {
      const provider = normalizeProvider(endpoint.providerType)
      if (!seen.has(provider)) {
        seen.add(provider)
        order.push(provider)
      }
    })
  })

  return order
}

const RpcGlobalOrderModal: React.FC<RpcGlobalOrderModalProps> = ({
  isOpen,
  onClose,
  snapshots,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const initialOrder = useMemo(() => buildInitialOrder(snapshots), [snapshots])
  const [order, setOrder] = useState<string[]>(initialOrder)

  useEffect(() => {
    if (isOpen) {
      setOrder(initialOrder)
    }
  }, [isOpen, initialOrder])

  const providerStats = useMemo(() => {
    const stats = new Map<
      string,
      {
        chains: Set<string>
        endpoints: number
      }
    >()

    snapshots.forEach(snapshot => {
      snapshot.endpoints.forEach(endpoint => {
        const provider = normalizeProvider(endpoint.providerType)
        if (!stats.has(provider)) {
          stats.set(provider, { chains: new Set<string>(), endpoints: 0 })
        }
        const entry = stats.get(provider)!
        entry.chains.add(snapshot.chainKey)
        entry.endpoints += 1
      })
    })

    return order.map(provider => {
      const entry = stats.get(provider)
      return {
        provider,
        chainCount: entry ? entry.chains.size : 0,
        endpointCount: entry ? entry.endpoints : 0,
      }
    })
  }, [snapshots, order])

  const moveProvider = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) {
      return
    }
    if (direction === 'down' && index === order.length - 1) {
      return
    }

    setOrder(prev => {
      const updated = [...prev]
      const swapIndex = direction === 'up' ? index - 1 : index + 1
      const temp = updated[index]
      updated[index] = updated[swapIndex]
      updated[swapIndex] = temp
      return updated
    })
  }

  const handleSubmit = () => {
    if (order.length === 0) {
      return
    }
    onSubmit(order)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('monitoring.rpc.globalOrderTitle')}</ModalHeader>
        <ModalCloseButton disabled={isSubmitting} />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text color="gray.500">
              {t('monitoring.rpc.globalOrderDescription')}
            </Text>

            <Box borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
              <VStack align="stretch" spacing={0}>
                {providerStats.length === 0 ? (
                  <Box p={4}>
                    <Text color="gray.500">
                      {t('monitoring.rpc.globalOrderEmpty')}
                    </Text>
                  </Box>
                ) : (
                  providerStats.map((item, index) => (
                    <Box
                      key={item.provider}
                      p={4}
                      borderBottomWidth={
                        index === providerStats.length - 1 ? '0' : '1px'
                      }
                      borderColor={borderColor}
                    >
                      <HStack justify="space-between" align="flex-start">
                        <VStack align="start" spacing={1}>
                          <HStack spacing={2}>
                            <Badge
                              colorScheme={
                                providerColorMap[item.provider] ?? 'gray'
                              }
                              variant="subtle"
                            >
                              {item.provider.toUpperCase()}
                            </Badge>
                            <Text fontWeight="medium">
                              {t('monitoring.rpc.globalOrderProviderUsage', {
                                chains: item.chainCount,
                                endpoints: item.endpointCount,
                              })}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.500">
                            {t('monitoring.rpc.globalOrderPosition', {
                              position: index + 1,
                            })}
                          </Text>
                        </VStack>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label={t('monitoring.rpc.moveUp')}
                            icon={<FiArrowUp />}
                            size="sm"
                            variant="ghost"
                            onClick={() => moveProvider(index, 'up')}
                            isDisabled={index === 0 || isSubmitting}
                          />
                          <IconButton
                            aria-label={t('monitoring.rpc.moveDown')}
                            icon={<FiArrowDown />}
                            size="sm"
                            variant="ghost"
                            onClick={() => moveProvider(index, 'down')}
                            isDisabled={
                              index === providerStats.length - 1 || isSubmitting
                            }
                          />
                        </HStack>
                      </HStack>
                    </Box>
                  ))
                )}
              </VStack>
            </Box>
            <Divider />
            <Text fontSize="sm" color="gray.500">
              {t('monitoring.rpc.globalOrderHint')}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={order.length === 0}
            >
              {t('monitoring.rpc.globalOrderApply')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RpcGlobalOrderModal
